import type { ResultOf } from '@graphql-typed-document-node/core';

import Skeleton from '@mui/material/Skeleton';

import {
  getFragmentData,
  graphql,
  ResolvableModeEnum,
} from '@e-socna/data-layer';
import { isDefined } from '@e-socna/utils';
import { EMPTY_STRING } from '@e-socna/empty';
import type { FragmentType, Id, UnitId } from '@e-socna/data-layer';

import { formatQuantity } from '../../utils/format-quantity.util';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { formatNumber } from '../../utils/format-number.util';
import { wrapExpression } from '../../utils/handlebars-expression.util';
import { N_A } from '../../constants/labels.constant';
import { checkIfIsId } from '../../utils/check-if-is-id.util';

import { useContentGenerator } from '../DocumentEditor/hooks/useContentGenerator.hook';

import { useResolvableNormalizer } from './hooks/useResolvableNormalizer';
import { PrimaryWrapper as PrimaryWrapperCmp } from './PrimaryWrapper';
import { SecondaryWrapper as SecondaryWrapperCmp } from './SecondaryWrapper';
import type { Props as BaseProps } from './types/props.type';

export const ResolvableQuantityCmp_ResolvableQuantity_Fragment = graphql(`
  fragment ResolvableQuantityCmp_ResolvableQuantity_Fragment on ResolvableQuantity {
    unitId {
      __typename
      mode
      resolverId
      value
    }
    value {
      __typename
      mode
      resolverId
      value
    }
  }
`);

export type ResolvableQuantityCmp_ResolvableQuantity = ResultOf<
  typeof ResolvableQuantityCmp_ResolvableQuantity_Fragment
>;

interface Props extends BaseProps {
  readonly resolvableQuantity: FragmentType<
    typeof ResolvableQuantityCmp_ResolvableQuantity_Fragment
  >;
}

export const ResolvableQuantity = ({
  PrimaryWrapper = PrimaryWrapperCmp,
  resolvableFieldSettings,
  resolvableQuantity: pResolvableQuantity,
  SecondaryWrapper = SecondaryWrapperCmp,
}: Readonly<Props>) => {
  const resolvableQuantity = getFragmentData(
    ResolvableQuantityCmp_ResolvableQuantity_Fragment,
    pResolvableQuantity,
  );

  const { unitId, value } = resolvableQuantity;
  const normalizedUnitId = useResolvableNormalizer(unitId);
  const normalizedValue = useResolvableNormalizer(value);

  const { unitsIdMap } = useReferencesContext();

  const { enableDirectGeneration, primaryMode } = resolvableFieldSettings;

  const { data: unitIdData, isSuccess: unitIdIsSuccess } = useContentGenerator({
    enabled:
      normalizedUnitId.mode === ResolvableModeEnum.Dynamic &&
      enableDirectGeneration,
    template:
      normalizedUnitId.mode === ResolvableModeEnum.Dynamic
        ? wrapExpression(normalizedUnitId.resolverCode)
        : EMPTY_STRING,
  });

  const { data: valueData, isSuccess: valueIsSuccess } = useContentGenerator({
    enabled:
      normalizedValue.mode === ResolvableModeEnum.Dynamic &&
      enableDirectGeneration,
    template:
      normalizedValue.mode === ResolvableModeEnum.Dynamic
        ? wrapExpression(normalizedValue.resolverCode)
        : EMPTY_STRING,
  });

  if (
    normalizedUnitId.mode === ResolvableModeEnum.Static &&
    normalizedValue.mode === ResolvableModeEnum.Static
  ) {
    if (
      !isDefined(normalizedUnitId.merged) ||
      !isDefined(normalizedValue.merged)
    ) {
      return <PrimaryWrapper>{N_A}</PrimaryWrapper>;
    }

    return (
      <PrimaryWrapper>
        {formatQuantity(
          {
            value: normalizedValue.merged,
            unitId: normalizedUnitId.merged,
          },
          unitsIdMap,
        )}
      </PrimaryWrapper>
    );
  }

  const merged =
    typeof normalizedValue.merged === 'number' &&
    checkIfIsId(normalizedUnitId.merged)
      ? formatQuantity(
          {
            value: normalizedValue.merged,
            unitId: normalizedUnitId.merged,
          },
          unitsIdMap,
        )
      : `${
          normalizedValue.merged
            ? typeof normalizedValue.merged === 'number'
              ? formatNumber(normalizedValue.merged)
              : wrapExpression(normalizedValue.merged)
            : N_A
        } ${
          normalizedUnitId.merged
            ? checkIfIsId(normalizedUnitId.merged)
              ? (unitsIdMap[normalizedUnitId.merged as UnitId]?.unit ?? N_A)
              : wrapExpression(normalizedUnitId.merged)
            : N_A
        }`.trim();

  if (!enableDirectGeneration) {
    return <PrimaryWrapper>{merged}</PrimaryWrapper>;
  }

  const isSuccess =
    (normalizedUnitId.mode === ResolvableModeEnum.Static || unitIdIsSuccess) &&
    (normalizedValue.mode === ResolvableModeEnum.Static || valueIsSuccess);

  if (!isSuccess) {
    return (
      <>
        <Skeleton sx={{ display: 'inline-block' }} variant="text" width={80} />{' '}
        <Skeleton sx={{ display: 'inline-block' }} variant="text" width={80} />
      </>
    );
  }

  const dynamicValue =
    normalizedValue.mode === ResolvableModeEnum.Dynamic &&
    isDefined(valueData?.generated)
      ? Number.parseFloat(valueData.generated)
      : normalizedValue.value;
  const dynamicUnitId =
    normalizedUnitId.mode === ResolvableModeEnum.Dynamic &&
    isDefined(unitIdData?.generated)
      ? (unitIdData.generated as Id)
      : normalizedUnitId.value;

  if (!isDefined(dynamicValue) || !isDefined(dynamicUnitId)) {
    return <PrimaryWrapper>{N_A}</PrimaryWrapper>;
  }

  const dynamic = formatQuantity(
    {
      value: dynamicValue,
      unitId: dynamicUnitId,
    },
    unitsIdMap,
  );

  return primaryMode === ResolvableModeEnum.Static ? (
    <>
      <PrimaryWrapper>{dynamic}</PrimaryWrapper>{' '}
      <SecondaryWrapper>{merged}</SecondaryWrapper>
    </>
  ) : (
    <>
      <PrimaryWrapper>{merged}</PrimaryWrapper>{' '}
      <SecondaryWrapper>{dynamic}</SecondaryWrapper>
    </>
  );
};
